import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Sidemodal from "../../common/sidemodal/sidemodal";
import Dropdown from "../../common/dropDown";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import {
  CAPA_RISK_OPTIONS,
  CAPA_ISF_TEMPLATE_CATEGORIES,
  normalizeIsfTemplateCategoryForApi,
} from "./capaConstants";
import { CAPA_STAGES } from "./capaWorkflow";
import { CAPA_LIST_FILTER_INITIAL } from "./capaListFilters";
import { fetchCapaConfig } from "./capaService";
import { mapCapaStaffOptions } from "./capaStaffUtils";

const MODULE_FILTER_OPTIONS = [{ value: "", label: "All modules" }];

const ISF_CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "All categories" },
  ...CAPA_ISF_TEMPLATE_CATEGORIES,
];

function optionFromValue(options, value) {
  const v = value == null ? "" : String(value);
  return options.find((o) => String(o.value) === v) ?? null;
}

/**
 * Side panel — CAPA list filters (cascade module → study/context like Create CAPA).
 */
const CapaListFilterDrawer = ({
  open,
  onClose,
  draftFilters,
  setDraftFilters,
  onApply,
  onClearFilters,
  uniqueOwners,
  userData,
}) => {
  const filterBodyRef = useRef(null);
  const contextSectionRef = useRef(null);
  const [studies, setStudies] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [documentOptions, setDocumentOptions] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [moduleFilterOptions, setModuleFilterOptions] = useState(MODULE_FILTER_OPTIONS);
  const [sourceTypeFilterOptions, setSourceTypeFilterOptions] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  const loadStudies = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    setLoadingStudies(true);
    try {
      const response = await api.post(API_ENDPOINTS.STUDY.GET_ALL_STUDIES, {
        OrganizationId: userData.OrganizationId,
        StaffIdRef: userData.StaffId,
        CreatedBy: userData.StaffId,
        Action: "GetStudyOrscheduledlist",
        AccessCode: "",
        Password: "",
      });
      if (response?.success === 1) {
        const rows = Array.isArray(response.data) ? response.data : [];
        const mapped = rows
          .map((row) => {
            const id = row?.StudyId ?? row?.StudyIdRef ?? row?.studyId ?? "";
            const num = row?.StudyNumber || row?.StudyCode || "";
            const name = row?.StudyName || "";
            const label =
              num && name ? `${num} — ${name}` : name || num || String(id);
            return { value: id, label };
          })
          .filter((o) => o.value);
        setStudies(mapped);
      }
    } finally {
      setLoadingStudies(false);
    }
  }, [userData]);

  const loadStaff = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    setLoadingStaff(true);
    try {
      const response = await api.post(API_ENDPOINTS.USER.GET_USERLIST, {
        OrganizationId: userData.OrganizationId,
        StaffId: userData.StaffId,
        Action: "Get",
        AccessCode: "",
        Password: "",
        HaveProfileViewRights: 1,
        HaveProfileEditRights: 0,
        HaveDelegationLogViewRights: 1,
        HaveDelegationLogEditRights: 0,
        HavingLineManagerRights: 1,
        HaveStudyTaskArchiveRights: 0,
        CreatedBy: userData.StaffId,
      });
      if (response?.success === 1) {
        const list = response.data?.UsersActivity || [];
        setStaffOptions(mapCapaStaffOptions(list));
      }
    } finally {
      setLoadingStaff(false);
    }
  }, [userData]);

  const loadIsfDocuments = useCallback(
    async (studyId, templateCategoryValue) => {
      const category = normalizeIsfTemplateCategoryForApi(templateCategoryValue);
      if (!userData?.OrganizationId || !studyId || !category) {
        setDocumentOptions([]);
        return;
      }
      setLoadingDocs(true);
      try {
        const response = await api.post(API_ENDPOINTS.ISF.GET_STUDY_FOLDERS, {
          OrganizationIdRef: userData.OrganizationId,
          ActionType: "GetAllFolder",
          StaudyIdRef: studyId,
          TemplateId: "",
          TemplateName: "",
          TemplateCategory: category,
        });
        const payload = response?.data != null ? response.data : response;
        const folderDocuments = Array.isArray(payload?.FolderDocuments)
          ? payload.FolderDocuments
          : [];
        const active = folderDocuments.filter(
          (item) => item.FileStatus === 0 || item.FileStatus === "0",
        );
        setDocumentOptions(
          active.map((item) => ({
            value: item.DocumentIdRef,
            label: item.FileName || item.DocumentIdRef,
          })),
        );
      } finally {
        setLoadingDocs(false);
      }
    },
    [userData],
  );

  useEffect(() => {
    if (!open || !userData) return;
    loadStudies();
    loadStaff();
  }, [open, userData, loadStudies, loadStaff]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!open || !userData?.OrganizationId) return;
      try {
        const cfg = await fetchCapaConfig(userData);
        const moduleFromApi = Array.isArray(cfg?.modules) ? cfg.modules : [];
        const mappedModules = moduleFromApi
          .map((v) => String(v || "").trim())
          .filter(Boolean)
          .map((v) => ({
            value: v,
            label:
              v === "study_management"
                ? "Study management"
                : v === "user_management"
                  ? "User management"
                  : v === "isf"
                    ? "ISF / eISF"
                    : v.replace(/_/g, " "),
          }));
        const mappedSourceTypes = (Array.isArray(cfg?.sourceTypes) ? cfg.sourceTypes : [])
          .map((v) => String(v || "").trim())
          .filter(Boolean)
          .map((v) => ({ value: v, label: v }));

        if (!cancelled) {
          setModuleFilterOptions(
            mappedModules.length > 0
              ? [{ value: "", label: "All modules" }, ...mappedModules]
              : MODULE_FILTER_OPTIONS,
          );
          setSourceTypeFilterOptions(mappedSourceTypes);
        }
      } catch {
        if (!cancelled) {
          setModuleFilterOptions(MODULE_FILTER_OPTIONS);
          setSourceTypeFilterOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userData]);

  const f = useMemo(() => {
    const raw = { ...CAPA_LIST_FILTER_INITIAL, ...draftFilters };
    const studyManagementStudyIds = Array.isArray(raw.studyManagementStudyIds)
      ? raw.studyManagementStudyIds
      : raw.studyFilter
        ? [String(raw.studyFilter)]
        : [];
    const documentFilters = Array.isArray(raw.documentFilters)
      ? raw.documentFilters
      : raw.documentFilter
        ? [String(raw.documentFilter)]
        : [];
    const ownerStaffIds = Array.isArray(raw.ownerStaffIds)
      ? raw.ownerStaffIds
      : [];
    return { ...raw, studyManagementStudyIds, documentFilters, ownerStaffIds };
  }, [draftFilters]);

  const mod = String(f.moduleFilter || "").trim();

  useEffect(() => {
    if (!open || mod !== "isf") return;
    if (f.isfStudyFilter && f.isfCategoryFilter) {
      loadIsfDocuments(f.isfStudyFilter, f.isfCategoryFilter);
    } else {
      setDocumentOptions([]);
    }
  }, [
    open,
    mod,
    f.isfStudyFilter,
    f.isfCategoryFilter,
    loadIsfDocuments,
  ]);

  const patch = (partial) =>
    setDraftFilters((prev) => ({
      ...CAPA_LIST_FILTER_INITIAL,
      ...prev,
      ...partial,
    }));

  const scrollToContext = useCallback(() => {
    const body = filterBodyRef.current;
    const target = contextSectionRef.current;
    if (!body || !target) return;
    body.scrollTo({
      top: Math.max(0, target.offsetTop - 10),
      behavior: "smooth",
    });
  }, []);

  const scrollToFilterBottom = useCallback(() => {
    const body = filterBodyRef.current;
    if (!body) return;
    body.scrollTo({
      top: body.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  const bottomDropdownOpenHandlers = {
    onMouseDown: scrollToFilterBottom,
    onTouchStart: scrollToFilterBottom,
    onFocusCapture: scrollToFilterBottom,
  };

  const setModuleFilter = (opt) => {
    const v = opt?.value != null ? String(opt.value) : "";
    patch({
      moduleFilter: v,
      studyManagementStudyIds: [],
      isfStudyFilter: "",
      staffFilter: "",
      isfCategoryFilter: "",
      documentFilters: [],
    });
    if (v) {
      window.requestAnimationFrame(scrollToContext);
    }
  };

  useEffect(() => {
    if (!open || !mod) return;
    window.requestAnimationFrame(scrollToContext);
  }, [open, mod, scrollToContext]);

  const toggleIn = (key, value) => {
    setDraftFilters((prev) => {
      const base = { ...CAPA_LIST_FILTER_INITIAL, ...prev };
      const arr = base[key] || [];
      const next = arr.includes(value)
        ? arr.filter((x) => x !== value)
        : [...arr, value];
      return { ...base, [key]: next };
    });
  };

  const isfStudyDropdownOptions = useMemo(
    () => [{ value: "", label: "All studies" }, ...studies],
    [studies],
  );

  const staffDropdownOptions = useMemo(
    () => [{ value: "", label: "All staff" }, ...staffOptions],
    [staffOptions],
  );

  const moduleSelected = useMemo(
    () =>
      optionFromValue(moduleFilterOptions, f.moduleFilter) || moduleFilterOptions[0],
    [f.moduleFilter, moduleFilterOptions],
  );

  const studyManagementMultiSelected = useMemo(() => {
    const ids = new Set((f.studyManagementStudyIds || []).map(String));
    return studies.filter((s) => ids.has(String(s.value)));
  }, [studies, f.studyManagementStudyIds]);

  const isfStudySelected = useMemo(
    () =>
      optionFromValue(isfStudyDropdownOptions, f.isfStudyFilter) ||
      isfStudyDropdownOptions[0],
    [isfStudyDropdownOptions, f.isfStudyFilter],
  );

  const staffSelected = useMemo(
    () =>
      optionFromValue(staffDropdownOptions, f.staffFilter) ||
      staffDropdownOptions[0],
    [staffDropdownOptions, f.staffFilter],
  );

  const isfCategorySelected = useMemo(
    () =>
      optionFromValue(ISF_CATEGORY_FILTER_OPTIONS, f.isfCategoryFilter) ||
      ISF_CATEGORY_FILTER_OPTIONS[0],
    [f.isfCategoryFilter],
  );

  const documentMultiSelected = useMemo(() => {
    const ids = new Set((f.documentFilters || []).map(String));
    return documentOptions.filter((d) => ids.has(String(d.value)));
  }, [documentOptions, f.documentFilters]);

  const ownerDropdownOptions = useMemo(
    () => staffOptions.map((s) => ({ value: s.value, label: s.label })),
    [staffOptions],
  );

  const ownerMultiSelected = useMemo(() => {
    const ids = new Set((f.ownerStaffIds || []).map(String));
    if (ids.size) {
      return ownerDropdownOptions.filter((o) => ids.has(String(o.value)));
    }
    // Backward-compat: map older ownerNames drafts to API options by label.
    const names = new Set((f.ownerNames || []).map(String));
    return ownerDropdownOptions.filter((o) => names.has(String(o.label)));
  }, [ownerDropdownOptions, f.ownerStaffIds, f.ownerNames]);

  return (
    <Sidemodal isOpen={open} onClose={onClose} sidewidth="28rem">
      <div
        className="capa-list-filter-panel d-flex flex-column flex-grow-1 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="capa-list-filter-title"
      >
        <div className="capa-list-filter-panel__header">
          <h2 id="capa-list-filter-title" className="h6 mb-0">
            Filter CAPA's
          </h2>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onApply();
          }}
        >
          <div className="capa-list-filter-panel__body" ref={filterBodyRef}>
            <section className="mb-3">
              <label className="d-flex align-items-center gap-2 small mb-0">
                <input
                  type="checkbox"
                  checked={Boolean(f.myCapasOnly)}
                  disabled={!userData?.StaffId}
                  onChange={(e) =>
                    patch({ myCapasOnly: e.target.checked })
                  }
                />
                <span>
                  My CAPAs only
                  {!userData?.StaffId ? (
                    <span className="text-muted"> (sign in to use)</span>
                  ) : null}
                </span>
              </label>
            </section>

            <section className="mb-3">
              <h3 className="small text-uppercase text-muted mb-2">Risk level</h3>
              <div className="d-flex flex-column gap-1">
                {CAPA_RISK_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="d-flex align-items-center gap-2 small mb-0"
                  >
                    <input
                      type="checkbox"
                      checked={f.risks.includes(opt.value)}
                      onChange={() => toggleIn("risks", opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </section>

           

            <section className="mb-3">
              <h3 className="small text-uppercase text-muted mb-2">Status</h3>
              <div className="d-flex flex-column gap-1 capa-list-filter-status-list">
                {CAPA_STAGES.map((st) => (
                  <label
                    key={st}
                    className="d-flex align-items-center gap-2 small mb-0"
                  >
                    <input
                      type="checkbox"
                      checked={f.statuses.includes(st)}
                      onChange={() => toggleIn("statuses", st)}
                    />
                    {st}
                  </label>
                ))}
              </div>
            </section>

            <section className="mb-3">
              <h3 className="small text-uppercase text-muted mb-2">Source</h3>
              <div className="d-flex flex-column gap-1">
                {sourceTypeFilterOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="d-flex align-items-center gap-2 small mb-0"
                  >
                    <input
                      type="checkbox"
                      checked={f.sourceTypes.includes(opt.value)}
                      onChange={() => toggleIn("sourceTypes", opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </section>

            <section className="mb-3">
              <h3 className="small text-uppercase text-muted mb-2">Owner</h3>
              <div {...bottomDropdownOpenHandlers}>
                <Dropdown
                  isMulti
                  options={ownerDropdownOptions}
                  selected={ownerMultiSelected}
                  onSelect={(items) =>
                    patch({
                      ownerStaffIds: Array.isArray(items)
                        ? items.map((x) => String(x.value))
                        : [],
                      ownerNames: Array.isArray(items)
                        ? items.map((x) => String(x.label))
                        : [],
                    })
                  }
                />
              </div>
              {loadingStaff && (
                <div className="small text-muted mt-1">Loading owners...</div>
              )}
            </section>

            <section className="mb-3">
              <h3 className="small text-uppercase text-muted mb-2">Created date</h3>
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label small mb-1">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={f.createdFrom}
                    onChange={(e) => patch({ createdFrom: e.target.value })}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={f.createdTo}
                    onChange={(e) => patch({ createdTo: e.target.value })}
                  />
                </div>
              </div>
            </section>

            <section className="mb-3" ref={contextSectionRef}>
              <h3 className="small text-uppercase text-muted mb-2">Context</h3>
              <label className="form-label small mb-1">Module</label>
              <div {...bottomDropdownOpenHandlers}>
                <Dropdown
                  options={moduleFilterOptions}
                  selected={moduleSelected}
                  onSelect={setModuleFilter}
                />
              </div>

              {mod === "study_management" && (
                <div className="mt-2">
                  <label className="form-label small mb-1">
                    Study (context)
                    {loadingStudies && (
                      <span className="ms-2 text-muted">Loading…</span>
                    )}
                  </label>
                  <div {...bottomDropdownOpenHandlers}>
                    <Dropdown
                      isMulti
                      options={studies}
                      selected={studyManagementMultiSelected}
                      onSelect={(items) =>
                        patch({
                          studyManagementStudyIds: Array.isArray(items)
                            ? items.map((x) => String(x.value))
                            : [],
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {mod === "user_management" && (
                <div className="mt-2">
                  <label className="form-label small mb-1">
                    Staff
                    {loadingStaff && (
                      <span className="ms-2 text-muted">Loading…</span>
                    )}
                  </label>
                  <div {...bottomDropdownOpenHandlers}>
                    <Dropdown
                      options={staffDropdownOptions}
                      selected={staffSelected}
                      onSelect={(opt) =>
                        patch({
                          staffFilter: opt?.value != null ? String(opt.value) : "",
                        })
                      }
                    />
                  </div>
                </div>
              )}

              {mod === "isf" && (
                <>
                  <div className="mt-2">
                    <label className="form-label small mb-1">
                      Study
                      {loadingStudies && (
                        <span className="ms-2 text-muted">Loading…</span>
                      )}
                    </label>
                    <div {...bottomDropdownOpenHandlers}>
                      <Dropdown
                        options={isfStudyDropdownOptions}
                        selected={isfStudySelected}
                        onSelect={(opt) =>
                          patch({
                            isfStudyFilter:
                              opt?.value != null ? String(opt.value) : "",
                            documentFilters: [],
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="form-label small mb-1">ISF category</label>
                    <div {...bottomDropdownOpenHandlers}>
                      <Dropdown
                        options={ISF_CATEGORY_FILTER_OPTIONS}
                        selected={isfCategorySelected}
                        onSelect={(opt) =>
                          patch({
                            isfCategoryFilter: opt?.value != null ? String(opt.value) : "",
                            documentFilters: [],
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="form-label small mb-1">
                      Context (document)
                      {loadingDocs && (
                        <span className="ms-2 text-muted">Loading…</span>
                      )}
                    </label>
                    <div {...bottomDropdownOpenHandlers}>
                      <Dropdown
                        isMulti
                        options={documentOptions}
                        selected={documentMultiSelected}
                        onSelect={(items) =>
                          patch({
                            documentFilters: Array.isArray(items)
                              ? items.map((x) => String(x.value))
                              : [],
                          })
                        }
                        disabled={!f.isfStudyFilter || !f.isfCategoryFilter}
                      />
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>

          <div className="capa-list-filter-panel__footer">
            <div className="ms-auto d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  if (onClearFilters) {
                    onClearFilters();
                  } else {
                    setDraftFilters(CAPA_LIST_FILTER_INITIAL);
                  }
                }}
              >
                Clear filters
              </button>
              <button type="submit" className="btn btn-primary btn-sm">
                Apply filters
              </button>
            </div>
          </div>
        </form>
      </div>
    </Sidemodal>
  );
};

export default CapaListFilterDrawer;
